import React, { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import {
    Box,
    Button,
    Card,
    Grid,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import createProposalModal from '../../createProposalModal';
import * as actions from '../actions';
import * as selectors from '../selectors';

import CFEGroupsListArray from './CFEGroupsListArray';

const CFEGroupsList = ({
    currentValues,
    initialValues,
    isEditionMode,
    roofTypes,
    setGridConfig,
    setHasConfigErrors,
    setInitialValues,
    segments,
    selectedSegmentId,
}) => {
    const { t } = useTranslation();
    const [handleAppendRef, setHandleAppendRef] = useState(null);

    const { control, reset, watch } = useForm({
        defaultValues: initialValues,
    });

    const formValues = watch();

    const { orientation, panel, system_uid, type_application } = {
        ...initialValues,
        ...currentValues,
    };

    let hasError = false;

    useEffect(() => {
        reset(initialValues);
    }, [initialValues, isEditionMode]);

    useEffect(() => {
        const newGroups = cloneDeep(formValues.groups);
        const modulesGroups = [];

        newGroups.forEach((group, index) => {
            for (let match = 0; match < group.match; match++) {
                for (let row = 0; row < group.row; row++) {
                    for (let col = 0; col < group.col; col++) {
                        const cell = 0;
                        const enable = true;
                        const group = match + index;
                        const internalId = `${group}-${row}-${col}-${cell}`;
                        modulesGroups.push({
                            col,
                            enable,
                            group,
                            internalId,
                            row,
                            cell,
                        });
                    }
                }
            }
        });

        setGridConfig({
            cols: 1,
            groups: newGroups,
            modulesGroups,
            rows: 1,
            selected: {},
        });
    }, [formValues]);

    useEffect(() => {
        setHasConfigErrors(hasError);
    }, [hasError]);

    return (
        <Card>
            <Card.Header>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h6">{`${t('Group list')} - ${formValues.name}`}</Typography>
                    <Button
                        onClick={() => handleAppendRef && handleAppendRef()}
                        variant="outlined"
                        endIcon={<AddIcon />}
                    >
                        {t('Add')}
                    </Button>
                </Box>
            </Card.Header>

            <Card.Body>
                <Box>
                    <Box sx={{ px: 1, display: { xs: 'none', md: 'block' } }}>
                        <Grid container>
                            <Grid size={{ sm: 3, md: 3 }}>
                                <Typography>
                                    <strong>{t('Rooftop')}</strong>
                                </Typography>
                            </Grid>
                            <Grid size={{ sm: 3, md: 3 }}>
                                <Typography>
                                    <strong>{t('System')}</strong>
                                </Typography>
                            </Grid>
                            <Grid size={{ sm: 2, md: 2.5 }}>
                                <Typography
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <strong>{t('Orientation')}</strong>
                                </Typography>
                            </Grid>
                            <Grid size={{ sm: 2 }}>
                                <Typography
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <strong>{t('Row', { count: 2 })}</strong>
                                </Typography>
                            </Grid>
                            <Grid size={{ sm: 2 }}>
                                <Typography
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <strong>{t('Column', { count: 2 })}</strong>
                                </Typography>
                            </Grid>
                            <Grid size={{ sm: 4, md: 2.5 }}>
                                <Typography
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <strong>{t('Repetitions')}</strong>
                                </Typography>
                            </Grid>
                            <Grid size={{ sm: 2, md: 2 }}>
                                <Typography>
                                    <strong>{t('Panel', { count: 2 })}</strong>
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <CFEGroupsListArray
                        control={control}
                        currentValues={currentValues}
                        disabled={!isEditionMode}
                        name="groups"
                        orientation={orientation}
                        panel={panel}
                        roofTypes={roofTypes}
                        setHasConfigErrors={setHasConfigErrors}
                        setInitialValues={setInitialValues}
                        system_uid={system_uid}
                        systemName={watch('system_name')}
                        type_application={type_application}
                        watch={watch}
                        segments={segments}
                        selectedSegmentId={selectedSegmentId}
                        setHandleAppendRef={setHandleAppendRef}
                    />
                </Box>
            </Card.Body>
        </Card>
    );
};

const mapStateToProps = createStructuredSelector({
    currentValues: selectors.getCurrentValues,
    roofTypes: createProposalModal.selectors.getRoofTypesData,
    selectedSegment: selectors.getSelectedSegment,
    segments: selectors.getSegmentsData,
    selectedSegmentId: selectors.getSelectedSegmentId,
});

const mapDispatchToProps = (dispatch) => ({
    setGridConfig: (values) => dispatch(actions.setGridConfig(values)),
    setHasConfigErrors: (value) =>
        dispatch(actions.setHasGroupsConfigErrors(value)),
    setInitialValues: (values) => dispatch(actions.setInitialValues(values)),
});

CFEGroupsList.propTypes = {
    currentValues: PropTypes.object,
    initialValues: PropTypes.object,
    isEditionMode: PropTypes.bool,
    roofTypes: PropTypes.array,
    setGridConfig: PropTypes.func,
    setHasConfigErrors: PropTypes.func,
    setInitialValues: PropTypes.func,
    segments: PropTypes.array,
    selectedSegmentId: PropTypes.string,
};

export default connect(mapStateToProps, mapDispatchToProps)(CFEGroupsList);
